import { Helmet } from "react-helmet-async";

/**
 * SoftwareApplication schema. NO aggregateRating, on purpose (2026-10-10).
 *
 * The block used to declare 4.7 from 1,323 ratings while the Play listing
 * showed 4.4 from ~1.69K. Self-serving rating markup that contradicts the
 * public listing is what earns a structured-data manual action, and it sat on
 * ~7 pages with no visible review content. If a rating ever returns here, it
 * must match the live Play listing and be visible on the page carrying it.
 *
 * This used to sit in public/index.html, which is the shell for every route, so
 * the rating rendered on all 22 pages. Google was showing "4.7 (1,323)" on the
 * Privacy Policy search result.
 *
 * Google's structured data policy requires rating markup to describe the
 * primary content of the page carrying it. A privacy policy, a terms page or a
 * blog article is not the app. Left as it was, this was a misrepresentation
 * risk, and a manual action would have removed rich results from the pages that
 * legitimately earn them, not just the offending ones.
 *
 * Mount this ONLY where the product itself is the subject of the page:
 * the homepage, the AI-astrologer page, the market and global landings, and the
 * tool pages. Do not add it to policy, about, founders, contact, how-it-works,
 * the blog index or blog articles.
 *
 * `operatingSystem` no longer claims iOS. There is no iOS app: no App Store URL
 * exists anywhere in the codebase, and the App Store button in the UI is a dead
 * control. Declaring iOS support in schema while shipping no iOS app is the
 * same class of problem as the rating placement.
 */
/* currency: the Offer's priceCurrency. Default INR; the US and Gulf market
   pages pass their own so the schema matches the market the page targets. */
export default function ProductSchema({ currency = "INR" }: { currency?: string }) {
  /* The MobileApplication node mirrors what every ranking competitor carries
     (HiAstro, Astroma). Same no-aggregateRating rule applies: the markup
     stays rating-free until a rating is shown visibly on the page AND
     matches the live Play listing. */
  const mobileApp = {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "@id": "https://astroaura.ai/#android-app",
    name: "Aura AI: AI Astrology & Kundli",
    operatingSystem: "Android",
    applicationCategory: "LifestyleApplication",
    installUrl:
      "https://play.google.com/store/apps/details?id=com.astroaura.auraai",
    offers: { "@type": "Offer", price: "0", priceCurrency: currency },
    publisher: { "@type": "Organization", name: "Aura AI" },
  };
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Aura AI - AI Vedic Astrology & Free Kundli",
    operatingSystem: "Android, Web",
    applicationCategory: "LifestyleApplication",
    url: "https://astroaura.ai/",
    description:
      "AI astrology app: chat with an AI Vedic astrologer, generate a free kundli and birth chart, and get your daily horoscope, kundli matching, mahadasha timeline and personalised remedies.",
    offers: { "@type": "Offer", price: "0", priceCurrency: currency },
    downloadUrl:
      "https://play.google.com/store/apps/details?id=com.astroaura.auraai",
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <script type="application/ld+json">{JSON.stringify(mobileApp)}</script>
    </Helmet>
  );
}
